import { useMutation, useQuery } from '@apollo/client'
import keys from 'lodash/keys'
import { useEffect, useState } from 'react'

import { gql } from '@/clients/utoniq-core'
import {
  CampaignEntryItemInput,
  CampaignEntryItemsInput,
  ConsoleCampaignDetailFragmentDoc,
  ConsoleCampaignDetailQuery,
  ConsoleCampaignDetailQueryVariables,
  CreateResourceMutation,
  CreateResourceMutationVariables,
  EntryCampaignImageTemplatesFragmentDoc,
  EntryCampaignMutation,
  EntryCampaignMutationVariables,
  EntryConditionItemFragment,
  ImageTemplateFragmentDoc,
  Maybe,
} from '@/clients/utoniq-core/schema'
import type { ConsoleCampaignItem } from '@/components/organisms'
import { FileType } from '@/components/organisms'
import { config } from '@/config'
import { useRouter, useTranslation, useUserTmpUploader } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera, File } from '@/core/native'
import { getPlatform } from '@/core/native/app'
import { compositeImage } from '@/lib/abyssale'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

const acceptType: Record<string, 'audio' | 'video'> = Object.assign(
  {},
  ...Object.entries(config.upload.mimeTypes.audio).map(([a, b]) => ({
    [b]: 'audio',
  })),
  ...Object.entries(config.upload.mimeTypes.video).map(([a, b]) => ({
    [b]: 'video',
  }))
)

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleCampaignDetail')
  const { router } = useRouter<{ campaignId: string }>()
  const { getImage } = Camera
  const { user } = useCurrentUser()
  const { data, loading } = useQuery<
    ConsoleCampaignDetailQuery,
    ConsoleCampaignDetailQueryVariables
  >(QUERY, {
    variables: {
      code: router.query.campaignId,
      artistId: user?.uid ?? '',
    },
  })

  const [compositing, setCompositing] = useState<boolean>(false)
  const { upload, uploading } = useUserTmpUploader(user?.uid ?? '')
  const { eyecatch } = useEyecatch(data)
  const [consoleCampaignItems] = useConsoleCampaignItems(data)

  const [entryCampaign] = useMutation<
    EntryCampaignMutation,
    EntryCampaignMutationVariables
  >(ENTRY_CAMPAIGN)

  const [createResource] = useMutation<
    CreateResourceMutation,
    CreateResourceMutationVariables
  >(CREATE_RESOURCE)

  return {
    state: {
      loading,
      artistId: user?.uid ?? '',
      campaign: data?.campaign ?? null,
      uploading: uploading || compositing,
      eyecatch,
      consoleCampaignItems,
      hasAvatarImage: !!data?.artist?.avatar?.uri,
    },
    actions: {
      async onUploadFile(fileType, index) {
        /**
         * 画像をアップロードした場合はテンプレートと合成してimage.frontを更新する
         * 動画・音声をアップロードした場合は benefit を更新する
         */
        if (fileType === 'image') {
          const file = await getImage(config.upload.mimeTypes.image)
          checkFileLimit(file, 'image')
          setCompositing(true)
          const uploadInfo = await upload(file)
          const template = data?.campaign?.imageTemplates?.items?.find(
            ({ rarity }) => rarity === consoleCampaignItems[index].rarity.id
          )?.front

          if (template) {
            const compositedImage = await compositeImage(template, file)
            setCompositing(false)
            return {
              fileUrl: compositedImage ?? uploadInfo.fileUrl,
              tmpPath: compositedImage ?? uploadInfo.tmpPath,
            }
          }
          setCompositing(false)
          return uploadInfo
        }

        const file = await getBenefit()
        checkFileLimit(file)
        checkBenefitFile(file)
        const { tmpPath } = await upload(file, false)

        return {
          uri: tmpPath,
          originalFile: file,
        }
      },
      /**
       * @todo フォーム送信してエントリーする機能の実装
       * https://app.clickup.com/t/908127/UT-5240
       */
      async onCampaignApply(inputs) {
        const items = await Promise.all(
          inputs.items.map(async item => {
            /**
             * @description
             * 特典の指定がない場合パターン
             * - wallpaper itemとして作成する。
             * - wallpaper resourceはアイテムの面面画像
             */
            if (!item.hasBenefit) {
              const uuid = await createUuid()
              const { data } = await createResource({
                variables: {
                  filePath: item.image.front.fileUrl,
                  name: uuid,
                },
              })

              return {
                type: 'wallpaper',
                rarity: item.rarity.id,
                thumbnail: item.image.front.fileUrl,
                thumbnailBack: item.image.back.uri,
                wallpaperResources: {
                  wallpaperResourceId: data?.createResource?.id,
                },
              }
            }

            /**
             * @description
             * 特典指定無し且つ、レアアイテムのパターン
             * - wallpaper itemとして作成する。
             * - wallpaper resourceはアイテムの面面画像
             */
            if (!item.benefit.originalFile) {
              const uuid = await createUuid()
              const { data } = await createResource({
                variables: {
                  filePath: item.image.front.fileUrl,
                  name: uuid,
                },
              })

              return {
                type: 'wallpaper',
                rarity: item.rarity.id,
                wallpaperResources: {
                  wallpaperResourceId: data?.createResource?.id,
                },
                thumbnail: item.image.front.fileUrl,
                thumbnailBack: item.image.back.uri,
              }
            }

            /**
             * @description
             * 特典指定有り（レアアイテム）のパターン
             * - 指定した特典に対応するリソースタイプのアイテムとして作成
             */
            const { data } = await createResource({
              variables: {
                filePath: item.benefit.uri as string,
                name: item.benefit.originalFile.name,
              },
            })

            return {
              type: item.benefit.type,
              rarity: item.rarity.id,
              resourceId: data?.createResource?.id,
              thumbnail: item.image.front.fileUrl,
              thumbnailBack: item.image.back.uri,
            }
          })
        )

        /**
         * input.itemsの形状に合うように整形
         */
        const inputItems = convertToCampaignEntryItemInput(
          items as CampaignEntryItemInput[]
        )

        if (!inputItems) throw new Error(t('itemConversionFailed'))

        await entryCampaign({
          variables: {
            id: router.query?.campaignId,
            inputs: { artistId: user?.uid ?? '', items: inputItems },
          },
        })

        return
      },
    },
  }
})

// キャンペーンのアイキャッチ画像を合成しurlを返す
function useEyecatch(data: ConsoleCampaignDetailQuery | undefined) {
  const [eyecatch, setEyecatch] = useState('')
  const eyecatchTemplate = data?.campaign?.imageTemplates?.gacha?.banner

  useEffect(() => {
    if (eyecatchTemplate && eyecatchTemplate?.templateId) {
      ;(async () => {
        const eyecatchImageUrl = await compositeImage(eyecatchTemplate)
        setEyecatch(eyecatchImageUrl)
      })()
    }
  }, [eyecatchTemplate])

  return { eyecatch }
}

// フォームに対応するアイテム達の状態を初期化して返す
function useConsoleCampaignItems(data: ConsoleCampaignDetailQuery | undefined) {
  const [consoleCampaignItems, setConsoleCampaignItems] = useState<
    ConsoleCampaignItem[]
  >([])
  const { t } = useTranslation('consoleCampaignDetail')
  const imageTemplates = data?.campaign?.imageTemplates?.items

  useEffect(() => {
    setConsoleCampaignItems(
      convertItems(data?.campaign?.entryCondition?.items ?? {}, t)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.campaign?.entryCondition?.items])

  // 裏面のテンプレート合成
  useEffect(() => {
    if (!(consoleCampaignItems.length && imageTemplates?.length)) return

    const backTemplates = {
      superrare: imageTemplates.find(({ rarity }) => rarity === 'superrare')
        ?.back,
      rare: imageTemplates.find(({ rarity }) => rarity === 'rare')?.back,
      common: imageTemplates.find(({ rarity }) => rarity === 'common')?.back,
    }

    ;(async () => {
      const backImageUrl = {
        superrare: backTemplates.superrare
          ? await compositeImage(backTemplates.superrare)
          : '',
        rare: backTemplates.rare
          ? await compositeImage(backTemplates.rare)
          : '',
        common: backTemplates.common
          ? await compositeImage(backTemplates.common)
          : '',
      }

      setConsoleCampaignItems(state =>
        state.map(item => ({
          ...item,
          image: {
            ...item.image,
            back: {
              uri: backImageUrl[item.rarity.id],
            },
          },
        }))
      )
    })()
  }, [imageTemplates, consoleCampaignItems.length])

  return [consoleCampaignItems, setConsoleCampaignItems] as const
}

// EntryConditionItemsをConsoleCampaignItemsに変換する
function convertItems(
  entryConditionItems: {
    common?: Maybe<EntryConditionItemFragment>
    rare?: Maybe<EntryConditionItemFragment>
    superrare?: Maybe<EntryConditionItemFragment>
  },
  t: (key: string) => void
) {
  const rarities = (['superrare', 'rare', 'common'] as const).filter(
    rarity => !!entryConditionItems[rarity]
  )

  return rarities
    .filter(
      rarity =>
        entryConditionItems &&
        Object.prototype.hasOwnProperty.call(entryConditionItems, rarity)
    )
    .map(rarity =>
      [...Array(entryConditionItems[rarity]?.count)]
        .map((_, index) => ({
          rarity: {
            id: rarity,
            name: `${t(rarity)}_${index + 1}`,
          },
          hasBenefit: rarity !== 'common',
          benefit: { uri: '', originalFile: null, type: FileType.Image },
          image: {
            front: {
              fileUrl: '',
              tmpPath: '',
            },
            back: { uri: '' },
          },
        }))
        .flat()
    )
    .flat()
}

// 音声・動画・PDFのファイルを取得する
function getBenefit() {
  return File.pickFile([keys(acceptType).toString()])
}

function checkFileLimit(
  file: File | File.PickFile,
  fileType?: 'image' | 'audio' | 'video'
): Error | void {
  const AcceptFileType =
    fileType || acceptType['mimeType' in file ? file.mimeType : file.type]
  const fileSizeLimit =
    AcceptFileType === 'video'
      ? config.upload.limit.video[getPlatform()]
      : config.upload.limit[AcceptFileType]

  if (file.size > fileSizeLimit) {
    throw new Error('uploadErrorMaximumSize')
  }
}

function checkBenefitFile(file: File.PickFile): Error | void {
  if (keys(acceptType).findIndex(o => o === file.mimeType) === -1) {
    throw new Error('uploadErrorAudioOrVideoOnly')
  }
}

/**
 * @description
 * input用にCampaignEntryItemInput型のアイテムをCampaignEntryItemsInput型に整形
 */
const convertToCampaignEntryItemInput = (items: CampaignEntryItemInput[]) => {
  if (!items?.length) return

  return items.reduce((acc, current) => {
    if (current.rarity === 'superrare') {
      return {
        ...acc,
        superrare: [...(acc?.superrare || []), { ...current }],
      }
    }

    if (current.rarity === 'rare') {
      return {
        ...acc,
        rare: [...(acc?.rare || []), { ...current }],
      }
    }

    return {
      ...acc,
      common: [...(acc?.common || []), { ...current }],
    }
  }, {} as CampaignEntryItemsInput)
}

const QUERY = gql`
  query ConsoleCampaignDetail($code: String, $artistId: String!) {
    artist {
      id
      avatar {
        uri
        smallUri
        mediumUri
      }
    }
    campaign(code: $code) {
      ...ConsoleCampaignDetail
      imageTemplates(artistId: $artistId) {
        ...EntryCampaignImageTemplates
      }
    }
  }
  ${ConsoleCampaignDetailFragmentDoc}
  ${ImageTemplateFragmentDoc}
  ${EntryCampaignImageTemplatesFragmentDoc}
`

const ENTRY_CAMPAIGN = gql`
  mutation EntryCampaign($id: ID!, $inputs: CampaignEntryInput!) {
    entryCampaign(id: $id, inputs: $inputs) {
      ...ConsoleCampaignDetail
    }
  }
  ${ConsoleCampaignDetailFragmentDoc}
`

const CREATE_RESOURCE = gql`
  mutation CreateResource($filePath: String!, $name: String!) {
    createResource(filePath: $filePath, name: $name) {
      id
      uri
      name
      type
      createdAt
    }
  }
`
